import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import SearchableSelect from '../../components/ui/SearchableSelect';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Loader2, Upload, X } from 'lucide-react';
import FileDropZone from '../../components/common/FileDropZone';
import { DocumentCategory } from '../../lib/api';
import { UploadFormData } from './types';
import { buildFolderOptions } from './utils';
import { VisibilityField } from './VisibilityField';
import { DOCUMENT_ACCEPT, DOCUMENT_TYPES_HINT } from './uploadValidation';

interface UploadDialogProps {
  open: boolean;
  uploading: boolean;
  categories: DocumentCategory[];
  uploadForm: UploadFormData;
  onClose: () => void;
  onFormChange: (form: UploadFormData) => void;
  /** Files dropped on / picked in the zone (validated by the caller). */
  onFileChange: (files: File[]) => void;
  onSubmit: (e: React.FormEvent) => void;
}

export function UploadDialog({
  open,
  uploading,
  categories,
  uploadForm,
  onClose,
  onFormChange,
  onFileChange,
  onSubmit,
}: UploadDialogProps) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      {/* Flex column with a fixed header/footer and an inner scrollable
          body. Previous layout put ``overflow-y-auto`` directly on
          DialogContent which made the dialog scroll AS A WHOLE — once a
          user picked multiple files the file list dropped below the
          fold and the footer Upload button was unreachable on small
          screens. Splitting the layout keeps the action buttons pinned
          and the only scroll happens inside the form body. */}
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col p-0">
        <DialogHeader className="px-6 pt-6">
          <DialogTitle>Upload Document</DialogTitle>
          <DialogDescription>
            Upload one or more documents, or add an external URL link.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 overflow-y-auto px-6 py-2 space-y-4">
          {/* Title — optional. When left blank the document is named after
              its file (or "External Link" for URL docs). Not required so a
              quick file upload doesn't force the user to type a title. */}
          <div className="space-y-2">
            <Label htmlFor="title">Title</Label>
            <Input
              id="title"
              value={uploadForm.title}
              onChange={(e) => onFormChange({ ...uploadForm, title: e.target.value })}
              placeholder="Optional — defaults to the file name"
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={uploadForm.description}
              onChange={(e) => onFormChange({ ...uploadForm, description: e.target.value })}
              placeholder="Enter document description (optional)"
              rows={3}
            />
          </div>

          {/* Folder (CR-5326: was "Category" — renamed for consistency).
              Searchable + hierarchical so nested subfolders (e.g. a folder
              per part number) read as subordinate to their parent and can
              be located by typing, instead of scrolling a flat list. */}
          <div className="space-y-2">
            <Label htmlFor="category">Folder</Label>
            <SearchableSelect
              options={buildFolderOptions(categories)}
              value={uploadForm.category}
              onChange={(value) => onFormChange({ ...uploadForm, category: value })}
              placeholder="Select a folder (optional)"
            />
          </div>

          {/* Document Type */}
          <div className="space-y-2">
            <Label>Document Type</Label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="document_type"
                  value="file"
                  checked={uploadForm.document_type === 'file'}
                  onChange={() => onFormChange({ ...uploadForm, document_type: 'file' })}
                  className="cursor-pointer"
                />
                <span>Upload File</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="document_type"
                  value="url"
                  checked={uploadForm.document_type === 'url'}
                  onChange={() => onFormChange({ ...uploadForm, document_type: 'url' })}
                  className="cursor-pointer"
                />
                <span>External URL</span>
              </label>
            </div>
          </div>

          {/* File Upload or URL Input */}
          {uploadForm.document_type === 'file' ? (
            <div className="space-y-2">
              <Label htmlFor="file">
                File(s) <span className="text-red-500">*</span>
              </Label>
              {/* Customer review 11 Sep (drag and drop everywhere). */}
              <div className="flex items-center gap-2">
                <FileDropZone
                  multiple
                  size="compact"
                  inputId="file"
                  accept={DOCUMENT_ACCEPT}
                  disabled={uploading}
                  hint="PDF, Office, text, CSV, archives or images — up to 20 files, 50MB each"
                  onFiles={onFileChange}
                  className="flex-1"
                  data-testid="document-upload-drop-zone"
                />
                {uploadForm.files.length > 0 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => onFormChange({ ...uploadForm, files: [] })}
                    title="Clear all files"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
              {uploadForm.files.length > 0 && (
                <div className="rounded-md border bg-muted/30 p-2 space-y-1 max-h-40 overflow-y-auto">
                  {uploadForm.files.map((f, idx) => (
                    <div
                      key={`${f.name}-${f.size}-${idx}`}
                      className="flex items-center justify-between gap-2 text-sm"
                    >
                      <span className="truncate" title={f.name}>
                        {f.name}{' '}
                        <span className="text-muted-foreground">
                          ({(f.size / 1024 / 1024).toFixed(2)} MB)
                        </span>
                      </span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 shrink-0"
                        onClick={() =>
                          onFormChange({
                            ...uploadForm,
                            files: uploadForm.files.filter((_, i) => i !== idx),
                          })
                        }
                        title={`Remove ${f.name}`}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                  {uploadForm.files.length > 1 && (
                    <p className="text-xs text-muted-foreground pt-1 border-t">
                      Multi-file upload: each file becomes its own document.
                      The Title field is ignored — each document is named
                      after its file.
                    </p>
                  )}
                </div>
              )}
              <p className="text-xs text-muted-foreground">
                Hold Cmd/Ctrl to select multiple (up to 20 files). Allowed
                types: {DOCUMENT_TYPES_HINT} (max 50MB each)
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              <Label htmlFor="external_url">
                External URL <span className="text-red-500">*</span>
              </Label>
              <Input
                id="external_url"
                type="url"
                value={uploadForm.external_url}
                onChange={(e) => onFormChange({ ...uploadForm, external_url: e.target.value })}
                placeholder="https://example.com/document.pdf"
                required={uploadForm.document_type === 'url'}
              />
            </div>
          )}

          {/* Additional Fields */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="tags">Tags</Label>
              <Input
                id="tags"
                value={uploadForm.tags}
                onChange={(e) => onFormChange({ ...uploadForm, tags: e.target.value })}
                placeholder="e.g., contract, invoice"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="version">Version</Label>
              <Input
                id="version"
                value={uploadForm.version}
                onChange={(e) => onFormChange({ ...uploadForm, version: e.target.value })}
                placeholder="e.g., 1.0, v2.1"
              />
            </div>
          </div>

          {/* Visibility (CR-5325: extracted to a shared VisibilityField so
              the Edit dialog reuses the same UX) */}
          <VisibilityField
            visibility={uploadForm.visibility}
            onVisibilityChange={(v) => onFormChange({ ...uploadForm, visibility: v })}
            sharedCompanyIds={uploadForm.shared_with_company_ids}
            onSharedCompanyIdsChange={(ids) => onFormChange({ ...uploadForm, shared_with_company_ids: ids })}
          />

          {/* CR-5329: Read & Accept toggle. When enabled, recipients are
              required to explicitly accept the document; for shared
              visibility the backend fires a notification to each user. */}
          <label className="flex items-start gap-3 cursor-pointer rounded-md border p-3 hover:bg-muted/30">
            <input
              type="checkbox"
              checked={uploadForm.requires_acceptance}
              onChange={(e) => onFormChange({ ...uploadForm, requires_acceptance: e.target.checked })}
              className="mt-0.5 cursor-pointer"
            />
            <div>
              <p className="text-sm font-medium">Require Read & Accept Receipt</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Recipients must open the document and confirm acceptance. Selected suppliers (Shared visibility) get notified automatically.
              </p>
            </div>
          </label>

          </div>
          {/* Footer pinned outside the scrollable body so the action
              buttons stay reachable no matter how many files are listed. */}
          <DialogFooter className="px-6 pb-6 pt-3 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={uploading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={uploading}>
              {uploading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Uploading{uploadForm.files.length > 1
                    ? ` ${uploadForm.files.length} files…`
                    : '…'}
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  {uploadForm.files.length > 1
                    ? `Upload ${uploadForm.files.length} Documents`
                    : 'Upload Document'}
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

